<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import { useWorkspaceStore } from "@/store";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { getRouter } from "@/router";
import "@maersk-global/mds-components-core/mc-loading-indicator";
import "@maersk-global/mds-components-core/mc-dialog";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-tooltip";
import "@maersk-global/mds-components-core/mc-menu";
import "@maersk-global/mds-components-core/mc-list";
import "@maersk-global/mds-components-core/mc-list-item";
import "@maersk-global/mds-components-core/mc-error";
import "@maersk-global/mds-components-core/mc-toast";
import { logError } from "@/loggers";
import { FEATURE_NAMES } from "@/loggers/types";

const { t } = useI18n();
const router = getRouter();

const workspaceStore = useWorkspaceStore();
const { workspaceTable, workspaceErrorMessage } = storeToRefs(workspaceStore);

const showDeleteWorkspaceModal = ref<boolean>(false);
const deleteWorkspaceInProgress = ref<boolean>(false);
const workspaceDeleteError = ref("");
const workspaceFetchError = ref("");
const workspaceToBeDeleted = ref(null);
const workspaceRefs = ref<HTMLDivElement[]>([]);
const toolTipRefs = ref<boolean[]>([]);
const loading = ref<boolean>(true);

onMounted(async () => {
  try {
    await workspaceStore.getWorkspaces();
  } catch (error) {
    workspaceFetchError.value = t("ERROR_LOADING_WORKSPACE");
    logError(FEATURE_NAMES.MVS_WORKSPACES, `Error fetching workspaces: ${error}`);
  } finally {
    loading.value = false;
    await nextTick();
    isTextOverflowing();
  }
});

const openModal = (workspaceToDelete) => {
  workspaceToBeDeleted.value = workspaceToDelete;
  showDeleteWorkspaceModal.value = true;
};

const closeModal = () => {
  showDeleteWorkspaceModal.value = false;
  workspaceToBeDeleted.value = null;
};

const deleteWorkspace = async () => {
  deleteWorkspaceInProgress.value = true;
  try {
    await workspaceStore.removeWorkspace(workspaceToBeDeleted.value.id);
    closeModal();
  } catch (error) {
    workspaceDeleteError.value = t("ERROR_DELETING_WORKSPACE");
    logError(FEATURE_NAMES.MVS_WORKSPACES, `Error deleting workspace: ${error}`);
  } finally {
    if (!workspaceDeleteError.value) {
      router.push({ path: "/" });
      deleteWorkspaceInProgress.value = false;
    }
  }
};

const isTextOverflowing = () => {
  nextTick(() => {
    workspaceRefs.value.forEach((element, index) => {
      if (element) {
        toolTipRefs.value[index] = element.offsetWidth < element.scrollWidth;
      }
    });
  });
};
</script>

<template>
  <div v-if="workspaceTable?.userFilters?.length" class="saved-workspace-list-container">
    <div v-if="loading"><mc-loading-indicator data-test="workspace/workspace-list-loading" /></div>
    <div v-else class="workspace-list">
      <div
        v-for="(workspace, index) in workspaceTable.userFilters"
        :key="workspace.id"
        class="workspace-item"
        data-test="workspace/workspace-list-item"
      >
        <!-- Router Link for Navigation -->
        <router-link
          :to="workspace.routePath + workspace.query"
          class="workspace-item__link"
          exact-active-class="workspace-item__link--active"
          active-class="workspace-item__link--active"
        >
          <div class="workspace-item__content">
            <mc-tooltip appearance="neutral-default" position="top-center" fit="small" :zindex="99999">
              <div slot="trigger" :ref="(el) => el && (workspaceRefs[index] = el as HTMLDivElement)" class="ellipsis">
                {{ workspace.name }}
              </div>
              <span v-if="toolTipRefs[index]">{{ workspace.name }}</span>
            </mc-tooltip>
            <!-- Menu for Actions -->
            <mc-menu class="link-menu" trigger="click" fit="small">
              <mc-button
                slot="trigger"
                fit="small"
                icon="ellipsis-horizontal"
                variant="plain"
                appearance="neutral"
                data-test="workspace/workspace-list-item-menu"
                hiddenlabel
                @click.prevent
              >
              </mc-button>
              <mc-list>
                <mc-list-item
                  data-test="workspace/workspace-list-item-delete-button"
                  :label="t('DELETE')"
                  @click="openModal(workspace)"
                ></mc-list-item>
              </mc-list>
            </mc-menu>
          </div>
        </router-link>
      </div>
    </div>
  </div>
  <!-- Delete Confirmation Modal -->
  <mc-dialog
    v-if="workspaceTable?.userFilters?.length"
    :open="showDeleteWorkspaceModal"
    data-test="workspace/delete-workspace-modal"
    :heading="t('DELETE')"
    :body="t('ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_WORKSPACE')"
    :zindex="99999"
    @closed.prevent.stop="closeModal"
  >
    <mc-error
      v-if="workspaceDeleteError || workspaceErrorMessage"
      invalid
      :errormessage="workspaceDeleteError || workspaceErrorMessage"
    />
    <mc-button
      slot="secondaryAction"
      appearance="neutral"
      fit="small"
      :label="t('CANCEL')"
      @click="closeModal"
    ></mc-button>
    <mc-button
      slot="primaryAction"
      fit="small"
      :label="t('DELETE')"
      :loading="workspaceStore.fetching"
      @click="deleteWorkspace"
    ></mc-button>
  </mc-dialog>

  <mc-toast
    v-if="workspaceFetchError || workspaceErrorMessage"
    :open="workspaceFetchError || workspaceErrorMessage"
    appearance="error"
    duration="5000"
    position="bottom-right"
  >
    <mc-notification :body="workspaceFetchError || workspaceErrorMessage"></mc-notification>
  </mc-toast>
</template>

<style scoped>
.saved-workspace-list-container {
  width: 100%;
  max-height: 40vh;
  overflow: auto;
}

.workspace-list {
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
}

.workspace-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  border-radius: var(--mds_brand_border_medium_radius);
  cursor: pointer;
}

.workspace-item__link {
  text-decoration: none;
  color: inherit;
  flex-grow: 1;
  padding: 0 8px;
  border-radius: var(--mds_brand_border_large_radius);
  max-height: 36px;
  height: 36px;
}

.workspace-item__link:hover {
  background-color: var(--mds_brand_appearance_neutral_weak_background-color);
}

.workspace-item__link--active {
  font-weight: bold;
  background-color: var(--mds_brand_appearance_neutral_weak_background-color);
  color: var(--mds_brand_appearance_neutral_weak_background-color);
}

.workspace-item__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: var(--mds_brand_typography_text_small_desktop_font-size);
}

.ellipsis {
  display: block;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.static-text {
  width: 100%;
  padding: 12px 0 12px 12px;
  position: sticky;
  top: 0;
  z-index: 10;
  color: var(--mds_brand_appearance_neutral_weak_text-color);
  background-color: var(--mds_brand_appearance_neutral_weakest_background-color);
}

.right-direction {
  padding: 12px 12px 12px 0;
}
</style>
